import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Eye, Building2 } from 'lucide-react';
import { companiesAPI } from '../../lib/api.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate } from '../../utils/formatters.js';

function CompaniesPage() {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    active: true
  });

  const queryClient = useQueryClient();

  const { data: companies, isLoading } = useQuery({
    queryKey: ['companies', search, activeFilter || 'all', pagination.page, pagination.limit],
    queryFn: () => companiesAPI.getAll({ search, active: activeFilter || undefined, page: pagination.page, limit: pagination.limit }),
    // Removed refetchInterval to prevent unnecessary API calls
    // refetchInterval: 30000,
  });

  const createMutation = useMutation({
    mutationFn: (data) => companiesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['companies']);
      setShowModal(false);
      resetForm();
      alert('Company created successfully');
    },
    onError: (error) => {
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Failed to create company';
      alert(`Create failed: ${msg}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => companiesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['companies']);
      setShowModal(false);
      resetForm();
      alert('Company updated successfully');
    },
    onError: (error) => {
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Failed to update company';
      alert(`Update failed: ${msg}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => companiesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['companies']);
    },
  });

  // Backend returns { data: [...], pagination: {...} }
  const companiesData = companies?.data?.data || [];
  const pageInfo = companies?.data?.pagination || { page: 1, limit: pagination.limit, total: 0, totalPages: 0, hasNext: false, hasPrev: false };

  // Fetch overall companies count (unfiltered)
  const { data: allCompaniesCount } = useQuery({
    queryKey: ['companies-total-count'],
    queryFn: () => companiesAPI.getAll({ page: 1, limit: 1 }),
    staleTime: 60000,
  });
  const totalAllCompanies = allCompaniesCount?.data?.pagination?.total || 0;

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      active: true
    });
    setEditingCompany(null);
  };

  const handleCreate = () => {
    setEditingCompany(null);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      code: company.code || '',
      active: company.active !== false
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // API requires only these three fields
    const submitData = { 
      name: formData.name,
      code: formData.code,
      active: formData.active
    };

    if (editingCompany) {
      updateMutation.mutate({ id: editingCompany.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Companies</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage company information</p>
        </div>
        <div className="flex items-center gap-6 ml-auto">
          <div className="text-right">
            <div className="text-2xl font-bold text-teal-700 dark:text-teal-300 leading-tight">{totalAllCompanies}</div>
            <div className="text-xs text-gray-600 dark:text-gray-300">All Companies</div>
          </div>
          <div className="flex items-center gap-2">
          <button
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            onClick={() => setShowFilters((s) => !s)}
            title="Toggle filters"
          >
            Filters
          </button>
          <Button variant="primary" onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New Company
          </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            placeholder="Search by name or code..."
            value={search}
            onChange={(e) => {
              setPagination((p) => ({ ...p, page: 1 }));
              setSearch(e.target.value);
            }}
            icon={<Search className="h-4 w-4" />}
          />
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">Active</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              value={activeFilter}
              onChange={(e) => {
                setPagination((p) => ({ ...p, page: 1 }));
                setActiveFilter(e.target.value);
              }}
            >
              <option value="">All</option>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </div>
          <div className="flex items-center md:justify-end">
            <button
              type="button"
              className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              onClick={() => {
                setSearch('');
                setActiveFilter('');
                setPagination({ page: 1, limit: 10 });
              }}
            >
              Clear filters
            </button>
          </div>
        </div>
      </Card>
      )}

      {/* Companies list styled like Designations/Users */}
      <Card className="w-full">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse h-8 bg-gray-100 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border border-teal-700">
              <thead>
                <tr className="bg-teal-700 border-b border-teal-600 dark:border-teal-600 divide-x divide-teal-600 dark:divide-teal-600">
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Code</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Active</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Created</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {companiesData.map((company) => (
                  <tr key={company.id} className="border-b border-teal-600 dark:border-teal-600 hover:bg-gray-50 dark:hover:bg-gray-800 divide-x divide-teal-600 dark:divide-teal-600">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-blue-100 dark:bg-blue-900 rounded-lg">
                          <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="font-medium text-gray-900 dark:text-white truncate">{company.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{company.code || company.company_code || company.companyCode || '—'}</td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{String(company.active)}</td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{company.created_at ? new Date(company.created_at).toLocaleDateString() : ''}</td>
                    <td className="py-3 px-4">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(company)} className="px-3 py-1.5 text-xs">
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {companiesData.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">No companies found</div>
            )}
          </div>
        )}
      </Card>

      {/* Pagination Footer */}
      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
        <div>
          {pageInfo.total > 0 && (
            <span>
              Showing {Math.min((pageInfo.page - 1) * pageInfo.limit + 1, pageInfo.total)} to {Math.min(pageInfo.page * pageInfo.limit, pageInfo.total)} of {pageInfo.total} companies
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            className={`px-3 py-1 rounded border ${pageInfo.hasPrev ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
            onClick={() => pageInfo.hasPrev && setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={!pageInfo.hasPrev}
          >
            Previous
          </button>
          <span>
            Page {pageInfo.page} of {Math.max(pageInfo.totalPages, 1)}
          </span>
          <button
            className={`px-3 py-1 rounded border ${pageInfo.hasNext ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
            onClick={() => pageInfo.hasNext && setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={!pageInfo.hasNext}
          >
            Next
          </button>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingCompany ? 'Edit Company' : 'New Company'}
              </h2>
              <Button variant="outline" size="sm" onClick={() => setShowModal(false)}>
                ×
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company Name
                </label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company Code
                </label>
                <Input
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="Enter company code"
                  required
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingCompany ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompaniesPage;
